import DOMPurify from 'dompurify';

/**
 * Sanitize HTML to prevent XSS attacks
 * Useful for rendering AI-generated content safely
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'u', 'br', 'p', 'ul', 'ol', 'li', 'span'],
    ALLOWED_ATTR: ['class'],
  });
};

/**
 * Convert markdown-style bold to HTML and automatically enhance readability
 * Used for AI summaries that use **text** for emphasis
 * Automatically highlights prices, percentages, addresses, and key metrics
 */
export const formatMarkdownToSafeHtml = (text: string): string => {
  if (!text) return '';
  
  let htmlContent = text;
  
  // First, convert **text** to <strong>text</strong> (preserve existing markdown)
  htmlContent = htmlContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Split text into paragraphs (double line breaks)
  const paragraphs = htmlContent.split(/\n\n+/).filter(p => p.trim());
  
  // Process each paragraph
  htmlContent = paragraphs
    .map(para => {
      let processed = para.trim();
      
      // Convert single line breaks to <br> within paragraphs
      processed = processed.replace(/\n/g, '<br>');
      
      // Helper to check if match is already inside HTML tags
      const isAlreadyTagged = (text: string, match: string): boolean => {
        // Check if the exact match is already inside <strong> tags
        if (text.includes(`<strong>${match}</strong>`)) {
          return true;
        }
        // Check if the match is already inside any span tag
        const matchIndex = text.indexOf(match);
        if (matchIndex !== -1) {
          const beforeMatch = text.substring(0, matchIndex);
          const lastOpenTag = beforeMatch.lastIndexOf('<');
          const lastCloseTag = beforeMatch.lastIndexOf('>');
          // If there's an unclosed tag before the match, we might be inside it
          if (lastOpenTag > lastCloseTag) {
            const tagStart = text.substring(lastOpenTag, matchIndex + match.length);
            // Check if we're inside a span or strong tag
            if (tagStart.includes('<span') || tagStart.includes('<strong')) {
              return true;
            }
          }
        }
        return false;
      };
      
      // Auto-highlight currency amounts (before other number processing)
      // Matches: $649,000, $330,000 to $549,900, $211 per square foot
      processed = processed.replace(/\$[\d,]+(?:\.\d{2})?(?:\s*(?:to|and|-)\s*\$[\d,]+(?:\.\d{2})?)?/g, (match) => {
        if (isAlreadyTagged(processed, match)) {
          return match;
        }
        return `<span class="highlight-value">${match}</span>`;
      });
      
      // Auto-highlight percentages
      processed = processed.replace(/(\d+(?:\.\d+)?%)/g, (match) => {
        if (isAlreadyTagged(processed, match)) {
          return match;
        }
        return `<span class="highlight-value">${match}</span>`;
      });
      
      // Auto-highlight addresses (street numbers and names)
      // Matches patterns like "3842 Sherbourne Dr" or "3913 Parkplace Dr"
      processed = processed.replace(/\b(\d{1,5}\s+[A-Z][a-z]+(?:\s+(?:Dr|St|Ave|Rd|Ln|Blvd|Way|Ct|Pl))?)\b/g, (match) => {
        if (isAlreadyTagged(processed, match)) {
          return match;
        }
        return `<span class="highlight-address">${match}</span>`;
      });
      
      // Auto-highlight key metrics (numbers with units like "per square foot", "sq ft", "days", etc.)
      processed = processed.replace(/\b(\d{1,3}(?:,\d{3})*(?:\.\d+)?)\s*(?:per\s+)?(?:square\s+foot|sq\s+ft|sqft|bed|bath|days?|months?|years?)\b/gi, (match) => {
        if (isAlreadyTagged(processed, match)) {
          return match;
        }
        return `<span class="highlight-metric">${match}</span>`;
      });
      
      // Auto-highlight standalone large numbers that might be important (prices without $, square footage, etc.)
      // Only highlight if they're 4+ digits and not already highlighted
      processed = processed.replace(/\b(\d{1,3}(?:,\d{3})+)\b/g, (match) => {
        if (isAlreadyTagged(processed, match)) {
          return match;
        }
        return `<span class="highlight-number">${match}</span>`;
      });
      
      return `<p class="summary-paragraph">${processed}</p>`;
    })
    .join('');
  
  return sanitizeHtml(htmlContent);
};

/**
 * Create safe props for dangerouslySetInnerHTML
 * This is a convenience wrapper to ensure all HTML is sanitized
 */
export const createSafeHtmlProps = (html: string) => ({
  dangerouslySetInnerHTML: { __html: sanitizeHtml(html) }
});

/**
 * Create safe props for markdown content (converts **bold** to <strong>)
 */
export const createSafeMarkdownProps = (text: string) => ({
  dangerouslySetInnerHTML: { __html: formatMarkdownToSafeHtml(text) }
});
